"use client";

import { toast } from "@workspace/ui/hooks/use-sonner-toast";
import { usePathname, useRouter } from "next/navigation";
import { type ReactNode, useCallback, useMemo } from "react";
import { SupportModal } from "@/components/feedback/support-modal";
import { LegalFooter } from "@/components/legal/legal-footer";
import { useSupabase } from "@/components/providers/supabase-auth-provider";
import { TopNav } from "@/components/top-nav";
import { useCurrentUserDisplay } from "@/hooks/shared";
import { useSupportModal } from "@/lib/utils/ui/support";

export default function PublicLayout({ children }: { children: ReactNode }) {
	const pathname = usePathname();
	const router = useRouter();
	const { user, isLoading, signOut } = useSupabase();
	const { data: userDisplay, isLoading: isLoadingDisplay } =
		useCurrentUserDisplay();
	const { isOpen, feedbackType, openSupportModal, closeSupportModal } =
		useSupportModal();

	const handleReportIssues = async () => {
		openSupportModal();
	};

	// Calculate navigation items for all users (authenticated and anonymous)
	const items = useMemo(() => {
		const isAuthenticated = !isLoading && !!user;

		// Prioritize fresh username data from auth user metadata over potentially stale userDisplay
		const freshUsername = user?.user_metadata?.username;
		const displayUsername = userDisplay?.username;
		const fallbackUsername = user?.email?.split("@")[0] || "";

		// Use the most recent username data available
		const username = freshUsername || displayUsername || fallbackUsername;
		const profileUrl = username ? `/${username}` : "/profile";

		const baseItems = [
			{
				title: "Festivals",
				url: "/festivals",
				isActive:
					pathname === "/festivals" ||
					(pathname?.startsWith("/festivals/") ?? false),
			},
			{
				title: "Shows",
				url: "/shows",
				isActive:
					pathname === "/shows" || (pathname?.startsWith("/shows/") ?? false),
			},
			{
				title: "Artists",
				url: "/artists",
				isActive:
					pathname === "/artists" ||
					(pathname?.startsWith("/artists/") ?? false),
			},
		];

		// Add profile for authenticated users
		if (isAuthenticated) {
			baseItems.push({
				title: "Profile",
				url: profileUrl,
				isActive:
					pathname === profileUrl ||
					(pathname?.startsWith(`${profileUrl}/`) ?? false) ||
					pathname === "/profile" ||
					(pathname?.startsWith("/profile/") ?? false),
			});
		}

		return baseItems;
	}, [pathname, user, userDisplay?.username, isLoading]);

	const handleSignOut = async () => {
		try {
			await signOut();
		} catch {
			toast({
				variant: "destructive",
				title: "Error",
				description:
					"Something went wrong while signing out. Please try again.",
			});
		}
	};

	const handleNavigateToFestival = (festivalSlug: string) => {
		router.push(`/festivals/${festivalSlug}`);
	};

	const handleNavigateToNavItem = useCallback(
		(url: string) => {
			// Type assertion for dynamic routes until typed routes are fully implemented
			router.push(url as Parameters<typeof router.push>[0]);
		},
		[router],
	);

	return (
		<div className="flex min-h-screen flex-col">
			<TopNav
				user={
					user
						? {
								name: userDisplay?.name || user.email?.split("@")[0] || "User",
								email: user.email || "",
								avatar: userDisplay?.avatar || "",
								username:
									user.user_metadata?.username ||
									userDisplay?.username ||
									undefined,
							}
						: null
				}
				isLoading={isLoading || isLoadingDisplay}
				items={items}
				onSignOut={handleSignOut}
				onNavigateToFestival={handleNavigateToFestival}
				onNavigateToNavItem={handleNavigateToNavItem}
				onReportIssues={handleReportIssues}
			/>
			<main className="flex flex-1 justify-center pt-[calc(3.5rem+env(safe-area-inset-top))]">
				<div className="w-full max-w-screen-xl">{children}</div>
			</main>
			<footer className="border-t bg-background/95 backdrop-blur-lg">
				<div className="container mx-auto flex items-center justify-center py-6">
					<LegalFooter variant="minimal" />
				</div>
			</footer>

			<SupportModal
				isOpen={isOpen}
				onClose={closeSupportModal}
				initialType={feedbackType}
			/>
		</div>
	);
}
